import { useNavigate } from "react-router-dom";
import { scrollToSection } from "../../utils/scroll";
import { handleSectionNavigation } from "../../utils/navigation";
import GoogleForm from "../../components/common/GoogleForm/GoogleForm";
import aboutImage from "../../assets/images/about/about.webp";
import { formatCNPJ, formatPhone } from "../../utils/formFormatters";
import "./Partners.css";

import {
  FaBuilding,
  FaIdCard,
  FaUser,
  FaEnvelope,
  FaPhoneAlt,
  FaUsers,
} from "react-icons/fa";

const BANKS = [
  "Caixa Econômica Federal",
  "Santander",
  "Banco do Brasil",
  "Banco Daycoval",
  "Banco BMG",
  "Banco Bradesco",
  "Banco C6",
  "Banco Itaú",
  "Banco Master",
  "Banco Mercantil",
  "Banco PAN",
  "Banco Safra",
  "Banco Facta",
  "Banco Icred",
  "Banrisul",
  "Banco Prata Digital",
  "Banco Olé Consignado",
];

const PARTNER_FORM_FIELDS = [
  {
    name: "company",
    googleName: "entry.1921821826",
    label: "Nome da empresa",
    type: "text",
    placeholder: "Digite o nome da empresa",
    required: true,
    icon: FaBuilding,
    autoComplete: "organization",
  },
  {
    name: "cnpj",
    googleName: "entry.2074325608",
    label: "CNPJ",
    type: "text",
    placeholder: "00.000.000/0000-00",
    required: true,
    maxLength: 18,
    icon: FaIdCard,
    format: formatCNPJ,
  },
  {
    name: "responsible",
    googleName: "entry.866377404",
    label: "Nome do responsável",
    type: "text",
    placeholder: "Digite o nome completo",
    required: true,
    icon: FaUser,
    autoComplete: "name",
  },
  {
    name: "email",
    googleName: "entry.468536221",
    label: "E-mail corporativo",
    type: "email",
    placeholder: "empresa@exemplo.com",
    required: true,
    icon: FaEnvelope,
    autoComplete: "email",
  },
  {
    name: "phone",
    googleName: "entry.376988718",
    label: "Telefone / WhatsApp",
    type: "tel",
    placeholder: "(00) 00000-0000",
    required: true,
    maxLength: 15,
    icon: FaPhoneAlt,
    autoComplete: "tel",
    format: formatPhone,
  },
  {
    name: "employees",
    googleName: "entry.451235967",
    label: "Quantos colaboradores sua empresa possui, aproximadamente?",
    type: "radio",
    required: true,
    icon: FaUsers,
    options: [
      "Até 10",
      "11 a 50",
      "51 a 100",
      "101 a 500",
      "Mais de 500",
    ],
  },
  {
    name: "audience",
    googleName: "entry.27732481",
    label: "Qual público você pretende direcionar para a parceria?",
    type: "checkbox",
    required: true,
    icon: FaUsers,
    options: [
      "Aposentados e pensionistas do INSS",
      "Servidores públicos",
      "Trabalhadores de empresas privadas",
      "Pessoas com contratos de crédito existentes",
      "Outros",
    ],
  },
];

const Partners = ({ onNavigate }) => {
  const navigate = useNavigate();

  const handleNavigation = (e, id) => {
    handleSectionNavigation(
      e,
      id,
      navigate,
      scrollToSection,
      onNavigate
    );
  };

  return (
    <>
      <section id="parceiros" className="section partners">
        <div className="container">
          <div className="partners-grid">
            <div className="partners-lead">
              <h2 className="title">Seja nosso parceiro</h2>

              <p className="subtitle">
                O Grupo Egel atua como correspondente bancário, conectando
                clientes a instituições financeiras e facilitando o acesso a
                diferentes soluções de crédito.
              </p>

              <p className="subtitle">
                Buscamos construir parcerias com empresas que desejam oferecer
                novas possibilidades aos seus clientes e colaboradores.
              </p>

              <a
                href="#contato-parceiros"
                className="btn btn-primary"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection("contato-parceiros", true);
                }}
              >
                Seja nosso parceiro
              </a>
            </div>

            <div className="partners-details">
              <article className="partner-block">
                <img
                  src={aboutImage}
                  alt="Parcerias com instituições financeiras"
                  className="partner-block-image"
                />

                <h3>Parcerias com instituições financeiras</h3>

                <p>
                  Trabalhamos com uma ampla rede de instituições financeiras,
                  oferecendo diferentes opções de produtos e condições,
                  sempre de acordo com as políticas de crédito de cada
                  instituição.
                </p>

                <p>Entre nossas instituições parceiras estão:</p>

                <ul className="bank-list">
                  {BANKS.map((bank) => (
                    <li className="tag" key={bank}>
                      {bank}
                    </li>
                  ))}
                </ul>
              </article>

              <article className="partner-block">
                <h3>Transparência e responsabilidade</h3>

                <p>
                  O Grupo Egel não é uma instituição financeira e não realiza
                  operações de crédito diretamente. A análise, aprovação e
                  definição das condições de cada operação são de
                  responsabilidade da instituição financeira escolhida,
                  conforme suas respectivas políticas de crédito.
                </p>

                <p>
                  Atuamos de acordo com as normas aplicáveis aos
                  correspondentes bancários e prezamos pela transparência,
                  segurança e proteção das informações fornecidas por nossos
                  clientes e parceiros.
                </p>
              </article>
            </div>
          </div>
        </div>
      </section>

      <section id="contato-parceiros" className="section partners-apply">
        <div className="container">
          <div className="partners-apply-content">
            <div className="partners-apply-intro">
              <h2 className="title">
                Quer fazer parte da nossa rede de parceiros?
              </h2>

              <p className="subtitle">
                Preencha o formulário com os dados da sua empresa. Nossa
                equipe analisará as informações e entrará em contato para
                apresentar as possibilidades de parceria.
              </p>
            </div>

            <div className="partners-apply-form-wrapper form-panel">
              <GoogleForm
                formUrl="https://docs.google.com/forms/d/e/1FAIpQLSen-4HFeV-J3isXIwLOwrSXc27sBQbWSMSbhfHktfa2vbgM0w/formResponse"
                notifyUrl="https://formsubmit.co/ajax/fernando@egelemprestimos.com.br"
                emailSubject="Novo parceiro via site"
                fields={PARTNER_FORM_FIELDS}
                fieldsPerPage={4}
                submitText="Quero ser parceiro"
                sendingText="Enviando..."
                successMessage="Cadastro enviado com sucesso!"
                receivedMessage="Cadastro recebido! Entraremos em contato em breve."
                className="partners-apply-form google-form-split"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Partners;